import React, { useState } from 'react';
import { Button, Group, Text, useMantineTheme } from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconUpload, IconX, IconFile } from '@tabler/icons-react';

import { v4 as uuidv4 } from 'uuid';
import { ITable, ITableColumn, ITableRow } from '@/types/Table';
import { notifications } from '@mantine/notifications';
import { UnifiedAppAPI } from '@/services/UnifiedAppAPI';
import { convertExcelToTable } from '@/services/Utility';
import { getFilesFromEvent } from '@/utils/dropzoneFiles';

interface TableUploaderProps {
  appId: string;
  type: 'metadata' | 'connector-selection';
  onUploadComplete?: (table: ITable) => void;
}

export function TableUploader({ appId, type, onUploadComplete }: TableUploaderProps) {
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);

  const handleDrop = async (files: File[]) => {
    if (files.length === 0) return;
    const file = files[0];

    if (!file.name.endsWith('.xlsx')) {
      notifications.show({
        title: 'Error',
        message: 'Please upload an Excel (.xlsx) file',
        color: 'red',
      });
      return;
    }

    setLoading(true);
    try {
      const table = await convertExcelToTable(file, true);

      const result =
        type === 'metadata'
          ? await UnifiedAppAPI.updateMetadata(appId, table)
          : await UnifiedAppAPI.updateConnector(appId, table);

      const response = result.app;
      if (response) {
        notifications.show({
          title: 'Table Upload Success',
          message: `Successfully uploaded ${type === 'metadata' ? 'metadata' : 'connector selection'} table for app "${response.appName}"`,
          color: 'green',
          autoClose: 5000,
          icon: <IconUpload size="1rem" />,
        });
        onUploadComplete?.(table);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process Excel file';

      notifications.show({
        title: 'Upload Failed',
        message: `Failed to upload ${type === 'metadata' ? 'metadata' : 'connector selection'} table: ${errorMessage}`,
        color: 'red',
        autoClose: 8000,
        icon: <IconX size="1rem" />,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dropzone
      onDrop={handleDrop}
      getFilesFromEvent={getFilesFromEvent}
      loading={loading}
      accept={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']}
      maxSize={5 * 1024 * 1024} // 5MB
      style={{
        borderStyle: 'dashed',
        borderWidth: '1px',
        borderRadius: '8px',
        padding: '12px',
      }}
    >
      <Group align="center" gap="xs" style={{ minHeight: 'auto', pointerEvents: 'none' }}>
        <Dropzone.Accept>
          <IconUpload size={20} stroke={1.5} color={theme.colors[theme.primaryColor][6]} />
        </Dropzone.Accept>
        <Dropzone.Reject>
          <IconX size={20} stroke={1.5} color={theme.colors.red[6]} />
        </Dropzone.Reject>
        <Dropzone.Idle>
          <IconFile size={20} stroke={1.5} color={theme.colors.gray[6]} />
        </Dropzone.Idle>

        <Text size="sm" fw={500} style={{ color: theme.colors.gray[7] }}>
          Drag or select Excel file (.xlsx)
        </Text>
      </Group>
    </Dropzone>
  );
}
